/**
 * components/UserPreferences/EmailPreferencesContent.tsx
 * Feature: 253 - Data-Driven Email Notification Preferences
 * Author: Peter Dobler (Dobler Data Solutions)
 * Constitution: v1.24.0 - Principle XLV (kit + tokens)
 *
 * Self-service email-preference UI embedded in the User Preferences modal's
 * Email tab. Category dropdown + per-target toggles, an all-on/all-off switch
 * per section, section description + frequency, per-item detail, and a DEV-only
 * "Send test email" button per section (hidden in production).
 */

import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Send } from 'lucide-react';
import { Select, Toggle, Badge, Button, LoadingState, ErrorState, EmptyState } from '../ui';
import {
  fetchMyPreferences,
  updateMyPreference,
  updateMyCategoryBulk,
  updateMyAllNotifications,
  sendTestNotificationEmail,
} from '../../services/notificationApi';
import { useEnvironment } from '../../modules/backup/hooks/useEnvironment';
import type { NotificationTargetingDimension, UpdatePreferenceRequest } from '@shared/types/notifications';

const PREFS_KEY = ['notification-preferences', 'me'] as const;

const DIMENSION_HINT: Record<NotificationTargetingDimension, string> = {
  global: 'A single switch for this kind of email.',
  alert_rule: 'On by default — turn off the alert rules you do not want to hear about.',
  app_job: 'Off by default — opt in to the apps/jobs you want to follow.',
  kpi: 'Choose which platform KPIs email you when they cross a threshold.',
};

const CATEGORY_FREQUENCY: Record<string, string> = {
  alert_escalation: 'One reminder when an alert first goes unacknowledged — no repeat nagging, and at most once per alert per day.',
  new_alert_digest: 'A single daily digest, only when new alerts were raised.',
  pipeline_monitoring: 'Sent when a followed app/job reports a new problem (checked roughly every 15 minutes).',
  pipeline_reminder: 'A weekly reminder, only when alerting pipelines exist that you have not opted into.',
  kpi: 'Checked hourly — one email each time a KPI crosses its threshold.',
};

export const EmailPreferencesContent: React.FC = () => {
  const queryClient = useQueryClient();
  const { environmentInfo } = useEnvironment();
  const isDevEnv = environmentInfo ? environmentInfo.environment !== 'production' : false;

  const [activeCategory, setActiveCategory] = useState<string>('');
  const [testing, setTesting] = useState<string | null>(null);
  const [testMsg, setTestMsg] = useState<string | null>(null);

  const { data, isLoading, isError, refetch } = useQuery({ queryKey: PREFS_KEY, queryFn: fetchMyPreferences });
  const invalidate = () => queryClient.invalidateQueries({ queryKey: PREFS_KEY });
  const mutation = useMutation({ mutationFn: (b: UpdatePreferenceRequest) => updateMyPreference(b), onSuccess: invalidate });
  const bulkMutation = useMutation({
    mutationFn: (v: { categoryKey: string; optedIn: boolean }) => updateMyCategoryBulk(v.categoryKey, v.optedIn),
    onSuccess: invalidate,
  });
  const allMutation = useMutation({
    mutationFn: (optedIn: boolean) => updateMyAllNotifications(optedIn),
    onSuccess: invalidate,
  });

  if (isLoading) return <LoadingState rows={6} />;
  if (isError) return <ErrorState title="Couldn't load your preferences" onRetry={() => refetch()} />;

  const categories = data?.categories ?? [];
  if (categories.length === 0) return <EmptyState title="No notification categories are configured" />;

  const activeKey =
    activeCategory && categories.some((c) => c.categoryKey === activeCategory)
      ? activeCategory
      : categories[0]?.categoryKey ?? '';
  const active = categories.find((c) => c.categoryKey === activeKey);

  const busy = mutation.isPending || bulkMutation.isPending || allMutation.isPending;
  const allOn = active ? active.items.length > 0 && active.items.every((i) => i.effective) : false;
  // Any category with at least one effective item ⇒ the master "all off" is actionable.
  const anyOn = categories.some((c) => c.items.some((i) => i.effective));

  const handleTest = async (categoryKey: string): Promise<void> => {
    setTesting(categoryKey);
    setTestMsg(null);
    try {
      const r = await sendTestNotificationEmail(categoryKey);
      setTestMsg(r.sent ? `Test email sent to ${r.to}.` : 'Test email was not sent (email delivery may be disabled).');
    } catch {
      setTestMsg('Failed to send test email.');
    } finally {
      setTesting(null);
    }
  };

  return (
    <div className="space-y-4">
      {/* Master switch — one click silences EVERY category below (the real "turn off all"). */}
      <div className="flex items-center justify-between gap-3 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-card)] p-3">
        <div className="min-w-0">
          <p className="text-sm font-medium text-[var(--color-text-primary)]">All email notifications</p>
          <p className="mt-0.5 text-xs text-[var(--color-text-secondary)]">
            {anyOn
              ? 'Turn off every category at once — you will receive no notification emails.'
              : 'All categories are currently off. Turn them back on, or enable individual types below.'}
          </p>
        </div>
        <Button
          size="sm"
          variant={anyOn ? 'primary' : 'outline'}
          disabled={busy}
          onClick={() => allMutation.mutate(!anyOn)}
        >
          {anyOn ? 'Turn all off' : 'Turn all on'}
        </Button>
      </div>

      <Select
        isFullWidth
        label="Notification type"
        value={activeKey}
        onChange={(e) => {
          setActiveCategory(e.target.value);
          setTestMsg(null);
        }}
        options={categories.map((c) => ({ value: c.categoryKey, label: c.displayName }))}
      />

      {active && (
        <div className="space-y-3">
          {/* Section description + frequency */}
          <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-subtle)] p-3">
            {active.description && <p className="text-sm text-[var(--color-text-primary)]">{active.description}</p>}
            <p className="mt-1 text-xs text-[var(--color-text-secondary)]">{DIMENSION_HINT[active.targetingDimension]}</p>
            {CATEGORY_FREQUENCY[active.categoryKey] && (
              <p className="mt-1 text-xs text-[var(--color-text-secondary)]">
                <span className="font-medium">Frequency:</span> {CATEGORY_FREQUENCY[active.categoryKey]}
              </p>
            )}
          </div>

          {/* All on/off (per section) + DEV send-test */}
          <div className="flex items-center justify-between gap-3">
            {active.targetingDimension !== 'global' && active.items.length > 0 ? (
              <label className="flex items-center gap-2">
                <Toggle
                  checked={allOn}
                  disabled={busy}
                  onChange={(next: boolean) => bulkMutation.mutate({ categoryKey: active.categoryKey, optedIn: next })}
                  aria-label={allOn ? 'Turn all off' : 'Turn all on'}
                />
                <span className="text-sm font-medium text-[var(--color-text-primary)]">
                  All {active.items.length} notifications
                </span>
              </label>
            ) : (
              <span />
            )}
            {isDevEnv && (
              <Button
                size="sm"
                variant="outline"
                leftIcon={<Send className="h-3.5 w-3.5" />}
                isLoading={testing === active.categoryKey}
                onClick={() => handleTest(active.categoryKey)}
              >
                Send test email
              </Button>
            )}
          </div>
          {testMsg && <p className="text-xs text-[var(--color-text-secondary)]">{testMsg}</p>}

          {/* Individual toggles */}
          {active.items.length === 0 ? (
            <EmptyState title="Nothing to configure here yet" />
          ) : (
            <ul className="divide-y divide-[var(--color-border)]">
              {active.items.map((item) => (
                <li key={item.targetKey} className="flex items-center justify-between gap-4 py-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="truncate text-sm text-[var(--color-text-primary)]">{item.label}</span>
                      {!item.isExplicit && (
                        <Badge variant="secondary" size="xs">
                          default
                        </Badge>
                      )}
                    </div>
                    {item.detail && <div className="truncate text-xs text-[var(--color-text-secondary)]">{item.detail}</div>}
                  </div>
                  <Toggle
                    checked={item.effective}
                    disabled={busy}
                    onChange={(next: boolean) =>
                      mutation.mutate({ categoryKey: active.categoryKey, targetKey: item.targetKey, optedIn: next })
                    }
                    aria-label={`${item.effective ? 'Disable' : 'Enable'} ${item.label}`}
                  />
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

export default EmailPreferencesContent;
